import React from "react";
import {
  Paper, Stack, Typography, Box, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";

import InfoButton from "../Clinical/InfoButton";
import CMStatCards from "../CareManager/CMStatCards";
import { getBHStatusColors } from "../../constants/bhDashboard";
import OutreachBHTable from "./OutreachBHTable";

/**
 * Panel 6: Care Programs & Engagement.
 * 3 program cards + enrollment by severity table + outreach list.
 */
export default function PanelBHCarePrograms({ data, loading, onCreateTask, helpContent }) {
  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Skeleton variant="rectangular" animation="wave" height={350} sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  const enrollmentBySeverity = data?.enrollmentBySeverity || [];
  const outreachList = data?.outreachList || [];
  const hasOutreach = outreachList.length > 0;

  const statItems = [
    { label: "Digital Well-Being", value: data?.digitalMentalWellBeing, color: "#2563EB", bgColor: "#EFF6FF" },
    { label: "Lucet BH Care", value: data?.lucetBHCare, color: "#7C3AED", bgColor: "#F3E8FF" },
    { label: "No Program", value: data?.noProgram, color: "#D97706", bgColor: "#FEF3C7" },
  ];

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 1.5,
              bgcolor: "#F3E8FF", color: "#7C3AED",
            }}
          >
            <GroupsIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Care Programs &amp; Engagement</Typography>
            <Typography variant="caption" color="text.secondary">BH program enrollment and patient outreach status</Typography>
          </Box>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Program Stat Cards */}
        <CMStatCards items={statItems} />

        {/* Enrollment by Severity Table */}
        {enrollmentBySeverity.length > 0 && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1, display: "block" }}>
              Enrollment by BH Severity
            </Typography>
            <TableContainer sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>BH Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>Digital</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>Lucet</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>Both</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>None</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enrollmentBySeverity.map((row) => {
                    const bhColors = getBHStatusColors(row.bhStatus);
                    return (
                      <TableRow key={row.bhStatus} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell sx={{ fontSize: "0.72rem" }}>
                          <Box
                            component="span"
                            sx={{
                              px: 0.8, py: 0.3, borderRadius: 1,
                              bgcolor: bhColors.bg, color: bhColors.text,
                              fontWeight: 600, fontSize: "0.68rem",
                            }}
                          >
                            {row.bhStatus}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                          {row.digital || <Typography component="span" variant="caption" color="text.disabled">-</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                          {row.lucet || <Typography component="span" variant="caption" color="text.disabled">-</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                          {row.both || <Typography component="span" variant="caption" color="text.disabled">-</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.72rem", fontWeight: 600, color: row.none > 0 ? "#D97706" : "text.primary" }}>
                          {row.none || <Typography component="span" variant="caption" color="text.disabled">-</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.72rem", fontWeight: 700 }}>
                          {row.total}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Outreach Priority List */}
        <Box>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1, display: "block" }}>
            Outreach Priority List ({data?.outreachTotal || outreachList.length} patients)
          </Typography>
          {hasOutreach ? (
            <OutreachBHTable patients={outreachList} onCreateTask={onCreateTask} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}>
              No outreach patients
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
